/**
 * MobileMeetingPrep — next/upcoming meeting prep surface (SLICE #7c-2, surface B).
 *
 * READ-ONLY and offline-aware. A single `useCachedQuery` reads, in priority order:
 *   1. `research_prebriefs` — the purpose-built meeting/calendar-event prebrief table. We take the
 *      next-upcoming row (meeting_starts_at >= now), else the most recent past one, and render its
 *      `prebrief_markdown` (agenda + brief) with the meeting time + attendee count.
 *   2. Fallback when there is no meeting prebrief: today's role digest from `research_role_prebriefs`
 *      (the current user's own rows), shown as "Today's prep" so a research user is never left blank.
 *
 * Both reads are folded into ONE query (cacheKey "mobile.meeting_prep") so the whole resolved
 * envelope is written through to the persistent cache as a unit. The queryFn THROWS on `{ error }`
 * so React Query keeps the cached placeholder (and serves it offline) instead of overwriting
 * last-known-good with a partial/empty value. Empty (no prep anywhere) is a valid, non-error state
 * that renders a quiet empty card — never an error banner.
 *
 * The Supabase generated types are stale for these tables, so the `.from(...)` calls are cast
 * through `any`. No writes. Web/desktop are untouched.
 */
import { useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronDown, FileText, BookOpen } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import MeetingPrepCard from '@/components/mobile/MeetingPrepCard';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

interface MeetingPrebriefRow {
  id: string;
  calendar_event_id: string | null;
  source_id: string | null;
  capsule_id: string | null;
  attendee_count: number | null;
  prebrief_markdown: string | null;
  meeting_starts_at: string | null;
  generated_at: string | null;
}

// A human-confirmed meeting capsule (research_meeting_capsules) — the consolidated background synthesis
// for a meeting. Only human_confirmed rows are surfaced (consolidated_summary present); 358 unreviewed
// drafts are deliberately excluded.
interface MeetingCapsuleRow {
  id: string;
  title: string | null;
  organisation: string | null;
  meeting_at: string | null;
  consolidated_summary: string | null;
  human_confirmed_at: string | null;
}

interface RolePrebriefRow {
  surface: string | null;
  target_date: string | null;
  digest_markdown: string | null;
  has_signal: boolean | null;
  generated_at: string | null;
}

// A meeting prep, normalised so the card is source-agnostic.
interface PreppedMeeting {
  id: string;
  title: string;
  meetingStartsAt: string | null;
  attendeeCount: number | null;
  markdown: string | null;
  isUpcoming: boolean;
  /** The captured source/transcript this prebrief was built from, if any (CAP-2.3 reader link). */
  sourceId: string | null;
  /** The linked meeting capsule (research_meeting_capsules.id), if this prebrief is tied to one. */
  capsuleId: string | null;
}

// A normalised "Meeting background" item — a human-confirmed capsule's consolidated summary.
interface BackgroundCapsule {
  id: string;
  title: string;
  whenLabel: string | null;
  summary: string;
  /** True when this capsule is the one linked to the primary meeting (vs general recent background). */
  linkedToPrimary: boolean;
}

// The resolved envelope cached as a unit. `source` records which path produced the content so the
// UI can label "Next meeting" vs "Today's prep" and so an empty cache still renders a coherent shell.
type PrepSource = 'meeting' | 'role-fallback' | 'none';

interface MeetingPrepData {
  source: PrepSource;
  // Populated when source === 'meeting'. The next-upcoming (or latest-past) meeting, plus any further
  // upcoming meetings to list compactly below it (full list — the UI shows the first few + an expander).
  primary: PreppedMeeting | null;
  upcoming: PreppedMeeting[];
  // Populated when source === 'role-fallback'.
  fallbackTitle: string | null;
  fallbackMarkdown: string | null;
  fallbackDate: string | null;
  // Human-confirmed meeting capsules — "Meeting background". Prefers the capsule linked to the primary
  // meeting; otherwise recent confirmed capsules as general background. Empty when none exist / no access.
  background: BackgroundCapsule[];
}

const EMPTY_PREP: MeetingPrepData = {
  source: 'none',
  primary: null,
  upcoming: [],
  fallbackTitle: null,
  fallbackMarkdown: null,
  fallbackDate: null,
  background: [],
};

// How many further upcoming meetings to keep (UI shows the first 4, then an "All upcoming" expander).
const UPCOMING_LIMIT = 15;
// How many background capsules to surface.
const BACKGROUND_LIMIT = 5;
// Trim a capsule summary so the card stays scannable.
const BACKGROUND_SUMMARY_CHARS = 320;

// ---------------------------------------------------------------------------
// Fetch (primary meeting prebrief, with today's role digest as fallback)
// ---------------------------------------------------------------------------

function meetingTitle(row: MeetingPrebriefRow): string {
  // The prebrief table has no title column; derive a stable, friendly label. Prefer the first
  // markdown heading/line, else a generic label.
  if (row.prebrief_markdown) {
    const line = row.prebrief_markdown
      .split('\n')
      .map((l) => l.replace(/^#{1,6}\s*/, '').replace(/^[-*]\s*/, '').trim())
      .find((l) => l.length > 0);
    if (line) return line.length > 80 ? `${line.slice(0, 79)}…` : line;
  }
  return 'Meeting prep';
}

// Human-confirmed meeting capsules → "Meeting background". Best-effort: a non-research caller (or an
// error) yields [] so the section simply doesn't render. The preferredId (the primary meeting's linked
// capsule) is hoisted to the top and flagged when present.
async function fetchBackground(preferredId: string | null): Promise<BackgroundCapsule[]> {
  try {
    const res = await (supabase.from('research_meeting_capsules') as any)
      .select('id, title, organisation, meeting_at, consolidated_summary, human_confirmed_at')
      .not('human_confirmed_at', 'is', null)
      .not('consolidated_summary', 'is', null)
      .order('meeting_at', { ascending: false })
      .limit(BACKGROUND_LIMIT);
    if (res.error) return [];
    const rows = (Array.isArray(res.data) ? res.data : []) as MeetingCapsuleRow[];

    const mapped: BackgroundCapsule[] = rows
      .filter((r) => r.consolidated_summary)
      .map((r) => {
        const summaryRaw = (r.consolidated_summary ?? '').replace(/\s+/g, ' ').trim();
        const summary =
          summaryRaw.length > BACKGROUND_SUMMARY_CHARS
            ? `${summaryRaw.slice(0, BACKGROUND_SUMMARY_CHARS).trimEnd()}…`
            : summaryRaw;
        const title = (r.title && r.title.trim()) || r.organisation || 'Meeting background';
        return {
          id: r.id,
          title,
          whenLabel: formatBackgroundWhen(r.meeting_at),
          summary,
          linkedToPrimary: preferredId != null && r.id === preferredId,
        };
      });

    // Hoist the capsule linked to the primary meeting to the top so it reads as "this meeting's background".
    mapped.sort((a, b) => Number(b.linkedToPrimary) - Number(a.linkedToPrimary));
    return mapped;
  } catch {
    return [];
  }
}

async function fetchMeetingPrep(): Promise<MeetingPrepData> {
  const nowIso = new Date().toISOString();

  // 1) Primary: meeting/calendar prebriefs. RLS gates this to research-read users; a non-research
  //    user simply gets [] (a valid empty state, not an error).
  const meetingRes = await (supabase.from('research_prebriefs') as any)
    .select(
      'id, calendar_event_id, source_id, capsule_id, attendee_count, prebrief_markdown, meeting_starts_at, generated_at',
    )
    .order('meeting_starts_at', { ascending: true })
    .limit(40);

  if (meetingRes.error) throw meetingRes.error;

  const meetingRows = (Array.isArray(meetingRes.data) ? meetingRes.data : []) as MeetingPrebriefRow[];

  if (meetingRows.length > 0) {
    const prepped: PreppedMeeting[] = meetingRows.map((r) => ({
      id: r.id,
      title: meetingTitle(r),
      meetingStartsAt: r.meeting_starts_at,
      attendeeCount: r.attendee_count,
      markdown: r.prebrief_markdown,
      isUpcoming: r.meeting_starts_at != null && r.meeting_starts_at >= nowIso,
      sourceId: r.source_id,
      capsuleId: r.capsule_id,
    }));

    // Next upcoming (rows are ascending by meeting_starts_at) else the most recent past.
    const upcoming = prepped.filter((p) => p.isUpcoming);
    const primary = upcoming[0] ?? prepped[prepped.length - 1];
    // Keep the FULL remaining upcoming list (capped generously) — the UI shows the first 4 then an
    // "All upcoming" expander for the rest.
    const rest = upcoming.filter((p) => p.id !== primary.id).slice(0, UPCOMING_LIMIT);

    const background = await fetchBackground(primary.capsuleId);

    return {
      source: 'meeting',
      primary,
      upcoming: rest,
      fallbackTitle: null,
      fallbackMarkdown: null,
      fallbackDate: null,
      background,
    };
  }

  // 2) Fallback: today's role digest (the current user's own rows). Never-blank guarantee for a
  //    research user when no meeting prep exists yet.
  const todayDate = format(new Date(), 'yyyy-MM-dd');
  const roleRes = await (supabase.from('research_role_prebriefs') as any)
    .select('surface, target_date, digest_markdown, has_signal, generated_at')
    .eq('target_date', todayDate)
    .order('generated_at', { ascending: false })
    .limit(5);

  if (roleRes.error) throw roleRes.error;

  const roleRows = (Array.isArray(roleRes.data) ? roleRes.data : []) as RolePrebriefRow[];
  // Prefer a row that actually has signal + markdown; else the freshest with markdown.
  const usable =
    roleRows.find((r) => r.has_signal && r.digest_markdown) ??
    roleRows.find((r) => r.digest_markdown) ??
    null;

  if (usable && usable.digest_markdown) {
    // No specific meeting → general recent background still adds value on the prep surface.
    const background = await fetchBackground(null);
    return {
      source: 'role-fallback',
      primary: null,
      upcoming: [],
      fallbackTitle: "Today's prep",
      fallbackMarkdown: usable.digest_markdown,
      fallbackDate: usable.target_date,
      background,
    };
  }

  // Nothing anywhere — a valid quiet empty state.
  return EMPTY_PREP;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatMeetingWhen(iso: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const abs = format(d, 'EEE d MMM, HH:mm');
    const rel = formatDistanceToNow(d, { addSuffix: true });
    return `${abs} · ${rel}`;
  } catch {
    return null;
  }
}

function formatFallbackWhen(dateStr: string | null): string | null {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'EEE d MMM');
  } catch {
    return dateStr;
  }
}

function formatBackgroundWhen(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// How many further upcoming meetings to show before the "All upcoming" expander.
const UPCOMING_PREVIEW = 4;

export default function MobileMeetingPrep() {
  const online = useIsOnline();
  // "All upcoming" expander — collapsed by default so the surface stays focused on the next meeting.
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  const prep = useCachedQuery<MeetingPrepData>({
    queryKey: ['mobile-meeting-prep'],
    cacheKey: 'mobile.meeting_prep',
    queryFn: fetchMeetingPrep,
  });

  // Never blank: fresh/placeholder, else the empty (but coherent) envelope.
  const data = prep.data ?? EMPTY_PREP;

  // "Showing saved data" affordance when serving last-known-good (offline or pre-fetch).
  const showStale = prep.isFromCache && prep.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && prep.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(prep.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Hard error only when we truly have nothing to show (no cache to fall back to). A signed-out /
  // permission error with no cached prep lands here; everything else renders normally.
  const hardError = prep.isError && !prep.isFromCache && prep.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-semibold text-primary">Meeting prep</h1>
        {showStale && (
          <p
            className={cn(
              'mt-1 text-[11px] font-medium uppercase tracking-wide',
              online ? 'text-muted-foreground/70' : 'text-amber-600',
            )}
          >
            {staleLabel}
          </p>
        )}
      </header>

      <div className="space-y-3">
        {hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Couldn’t load
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load your meeting prep just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved prep yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : data.source === 'meeting' && data.primary ? (
          <>
            <MeetingPrepCard
              kicker={data.primary.isUpcoming ? 'Next meeting' : 'Most recent meeting'}
              title={data.primary.title}
              whenLabel={formatMeetingWhen(data.primary.meetingStartsAt)}
              attendeeCount={data.primary.attendeeCount}
              markdown={data.primary.markdown}
            />

            {/* CAP-2.3: link to the captured source transcript (+ vocal-emotion overlay) when this
                prebrief was built from one. Only renders when a source_id is present. */}
            {data.primary.sourceId && (
              <Link
                to={`/partner/m/source/${data.primary.sourceId}`}
                className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm font-medium text-primary"
              >
                <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
                View transcript
              </Link>
            )}

            {data.upcoming.length > 0 && (
              <section className="rounded-xl border border-border bg-card p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                  Also coming up
                </p>
                <ul className="mt-2 space-y-2">
                  {(showAllUpcoming ? data.upcoming : data.upcoming.slice(0, UPCOMING_PREVIEW)).map(
                    (m) => {
                      const when = formatMeetingWhen(m.meetingStartsAt);
                      return (
                        <li key={m.id} className="rounded-lg bg-muted/40 p-3">
                          <p className="font-heading text-sm font-semibold">{m.title}</p>
                          {when && (
                            <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                              {when}
                            </p>
                          )}
                        </li>
                      );
                    },
                  )}
                </ul>

                {/* "All upcoming" expander — only when there are more than the preview slice. */}
                {data.upcoming.length > UPCOMING_PREVIEW && (
                  <button
                    type="button"
                    onClick={() => setShowAllUpcoming((v) => !v)}
                    aria-expanded={showAllUpcoming}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary active:opacity-80"
                  >
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        showAllUpcoming && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                    {showAllUpcoming
                      ? 'Show fewer'
                      : `All upcoming (${data.upcoming.length})`}
                  </button>
                )}
              </section>
            )}
          </>
        ) : data.source === 'role-fallback' && data.fallbackMarkdown ? (
          <>
            <p className="text-sm text-muted-foreground">
              No upcoming meeting prep yet — here’s today’s brief instead.
            </p>
            <MeetingPrepCard
              kicker="Today"
              title={data.fallbackTitle ?? "Today's prep"}
              whenLabel={formatFallbackWhen(data.fallbackDate)}
              markdown={data.fallbackMarkdown}
            />
          </>
        ) : (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CalendarClock className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">No meeting prep yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              When you have an upcoming meeting, its agenda and the relevant brief will appear here.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        )}

        {/* ---------- Meeting background — human-confirmed capsule summaries ----------
            Shows the consolidated background synthesis for the meeting. The capsule LINKED to the primary
            meeting (research_prebriefs.capsule_id → research_meeting_capsules.id) is hoisted to the top
            and flagged; otherwise these are recent confirmed capsules as general background. Only
            human_confirmed rows are surfaced. Renders whenever any background exists (in either source
            branch) and we're not in the hard-error state. */}
        {!hardError && data.background.length > 0 && (
          <section className="rounded-xl border border-border bg-card p-4" aria-labelledby="meeting-background">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BookOpen className="h-4 w-4" aria-hidden="true" />
              </span>
              <h2 id="meeting-background" className="font-heading text-sm font-semibold">
                Meeting background
              </h2>
            </div>
            <ul className="space-y-3">
              {data.background.map((c) => (
                <li key={c.id} className="rounded-lg bg-muted/40 p-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="min-w-0 font-heading text-sm font-semibold">{c.title}</p>
                    {c.linkedToPrimary && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        This meeting
                      </span>
                    )}
                  </div>
                  {c.whenLabel && (
                    <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                      {c.whenLabel}
                    </p>
                  )}
                  <p className="mt-1.5 text-sm text-muted-foreground">{c.summary}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
